import { useEffect, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE } from "../../constants/FirestoreConstant";

/**
 * Hook to fetch and manage purchase order invoices from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {string} options.status - Filter by status (optional)
 * @param {string} options.supplierId - Filter by supplier ID (optional)
 * @param {string} options.shipmentId - Filter by shipment ID (optional)
 * @returns {Object} - Invoices data and state
 */
export const usePurchaseOrderInvoices = (options = {}) => {
  const { realtime = true, status = null, supplierId = null, shipmentId = null } = options;
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const firestore = useFirestore();

  useEffect(() => {
    setLoading(true);
    let query = firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE);

    // Apply filters
    if (status) {
      query = query.where("status", "==", status);
    }
    if (supplierId) {
      query = query.where("supplierId", "==", supplierId);
    }
    if (shipmentId) {
      query = query.where("shipmentId", "==", shipmentId);
    }

    // Order by creation date
    query = query.orderBy("createdAt", "desc");

    if (realtime) {
      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setInvoices(data);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error("Error fetching purchase order invoices:", err);
          setError(err);
          setLoading(false);
        },
      );

      return () => unsubscribe();
    } else {
      query
        .get()
        .then((snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setInvoices(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching purchase order invoices:", err);
          setError(err);
          setLoading(false);
        });
    }
  }, [firestore, realtime, status, supplierId, shipmentId]);

  return { invoices, loading, error };
};
